// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import { useCallback, useEffect, useState } from 'react';
import {
  clearResolvedDuplicates,
  errorRowIndexes,
  hasErrors,
  rowHasErrors,
} from '../utils/rowErrors';
import { RowErrors } from '../utils/rowErrors';
import { ApiError, SaveFailure } from '../utils/serverErrors';

/**
 * The edit state of a versioned list shown in a ListTable: changing, adding and removing rows,
 * their errors, the table's filter, and saving the full set. Used by Common/ArcRows.
 */

export interface RowEditorOptions<R, F extends string> {
  // The rows as loaded; must be stable between renders, and a new list resets the editor.
  loadedRows: R[];
  newRow: () => R;
  validate: (rows: R[]) => RowErrors<F>[];
  // Fields that make up the key `keyOf` builds, such as the locales of an arc. Row-level errors
  // are about that key, e.g. a duplicate, so only changing one of these fields can resolve them.
  keyFields: readonly F[];
  keyOf: (row: R) => string;
  // Shown when saving with no rows.
  emptyMessage: string;
  // Rejects with the API error when the save fails.
  onSave: (rows: R[]) => Promise<unknown>;
  describeSaveError: (error: ApiError, rowCount: number) => SaveFailure<F>;
}

export interface RowEditor<R, F extends string> {
  editing: boolean;
  startEditing: () => void;
  // Discards every edit and leaves edit mode.
  reset: () => void;
  rows: R[];
  rowErrors: RowErrors<F>[];
  // 1-based numbers of the rows that have errors.
  errorRowNumbers: number[];
  errorFor: (index: number, field: F | 'row') => string | undefined;
  formErrors: string[];
  conflict: string[] | null;
  submitting: boolean;
  filteringText: string;
  setFilteringText: (filteringText: string) => void;
  // The row the table should scroll to, a new object per request; null until one is asked for.
  revealedRow: { index: number } | null;
  // Clears the filter and reveals the first row with an error.
  showFirstError: () => void;
  changeRow: (index: number, field: F, value: string | null) => void;
  addRow: () => void;
  removeRow: (index: number) => void;
  save: () => void;
}

export function useRowEditor<R, F extends string>({
  loadedRows,
  newRow,
  validate,
  keyFields,
  keyOf,
  emptyMessage,
  onSave,
  describeSaveError,
}: RowEditorOptions<R, F>): RowEditor<R, F> {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<R[]>(loadedRows);
  const [rowErrors, setRowErrors] = useState<RowErrors<F>[]>([]);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [conflict, setConflict] = useState<string[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [filteringText, setFilteringText] = useState('');
  const [revealedRow, setRevealedRow] = useState<{ index: number } | null>(null);

  const reset = useCallback((): void => {
    setEditing(false);
    setRows(loadedRows);
    setRowErrors(loadedRows.map(() => ({})));
    setFormErrors([]);
    setConflict(null);
  }, [loadedRows]);

  useEffect(() => {
    reset();
    setFilteringText('');
  }, [reset]);

  const revealError = (index: number): void => {
    setFilteringText('');
    setRevealedRow({ index });
  };

  const changeRow = (index: number, field: F, value: string | null): void => {
    const updated = rows.map((row, i) => (i === index ? { ...row, [field]: value } : row));
    const cleared = rowErrors.map((errors, i) =>
      i === index ? { ...errors, [field]: undefined } : errors
    );
    setRows(updated);
    if (!keyFields.includes(field)) {
      setRowErrors(cleared);
      return;
    }
    cleared[index] = { ...cleared[index], row: undefined };
    setRowErrors(clearResolvedDuplicates(updated, cleared, keyOf, keyOf(rows[index])));
  };

  const addRow = (): void => {
    setRows([...rows, newRow()]);
    setRowErrors([...rowErrors, {}]);
    setRevealedRow({ index: rows.length });
  };

  const removeRow = (index: number): void => {
    const updated = rows.filter((_, i) => i !== index);
    const remaining = rowErrors.filter((_, i) => i !== index);
    setRows(updated);
    setRowErrors(clearResolvedDuplicates(updated, remaining, keyOf, keyOf(rows[index])));
  };

  const showRowErrors = (errors: RowErrors<F>[]): void => {
    setRowErrors(rows.map((_, index) => errors[index] || {}));
    const first = errors.findIndex(rowHasErrors);
    if (first >= 0) {
      revealError(first);
    }
  };

  const save = (): void => {
    const validationErrors = validate(rows);
    setConflict(null);
    showRowErrors(validationErrors);
    if (rows.length === 0) {
      setFormErrors([emptyMessage]);
      return;
    }
    setFormErrors([]);
    if (hasErrors(validationErrors)) {
      return;
    }
    setSubmitting(true);
    onSave(rows)
      .catch((error: ApiError) => {
        const failure = describeSaveError(error, rows.length);
        setConflict(failure.conflict);
        setFormErrors(failure.formErrors);
        showRowErrors(failure.rowErrors);
      })
      .finally(() => setSubmitting(false));
  };

  return {
    editing,
    startEditing: (): void => setEditing(true),
    reset,
    rows,
    rowErrors,
    errorRowNumbers: errorRowIndexes(rowErrors).map(index => index + 1),
    errorFor: (index, field): string | undefined => rowErrors[index] && rowErrors[index][field],
    formErrors,
    conflict,
    submitting,
    filteringText,
    setFilteringText,
    revealedRow,
    showFirstError: (): void => {
      const [first] = errorRowIndexes(rowErrors);
      if (first !== undefined) {
        revealError(first);
      }
    },
    changeRow,
    addRow,
    removeRow,
    save,
  };
}
